<template>
  <div
    v-for="(payment, index) in billPayments"
    :key="payment.id"
    class="flex py-2 items-center text-sm text-n-0 font-body"
  >
    <div class="flex-1 capitalize">
      <div>
        {{ t(`bill.${payment.type}`, payment.type) }}
      </div>
      <div v-if="(payment.tip || 0) > 0" class="text-xs">
        {{ t('checkout.tip') }}
      </div>
    </div>
    <div class="text-right font-bold">
      <div>
        {{ currency(payment.amount - (payment.tip || 0)) }}
      </div>
      <div v-if="(payment.tip || 0) > 0">
        {{ currency(payment.tip) }}
      </div>
    </div>
    <l-dropdown
      v-model:open="openOptions[index]"
      :mark-selected="false"
      :options="getOptionsDataphone(payment.id)"
    >
    </l-dropdown>
    <l-icon
      v-if="isDataphonePayment(payment.id)"
      name="3-dots"
      size="medium"
      class="text-v-300 p-1 ml-2 cursor-pointer"
      @click.stop="openOptions[index] = true"
    />
    <l-icon
      v-else
      name="close"
      size="medium"
      class="text-v-300 ml-3 p-1 cursor-pointer"
      @click="deletePaymentWithConfirmation(payment.id)"
    />
  </div>
  <div v-if="billPayments.length === 0" class="text-n-0 text-sm py-2">
    {{ t('checkout.no-payments') }}
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { LDropdown, LIcon, useDialog } from '@last/core-ui/paprika'
import type { Option } from '@last/core-ui/paprika/components/dropdown/types'

import { useMoney } from '@/composables/useMoney'
import TicketPrinter from '@/integrations/printer/ticketPrinter'
import { useTabsStore } from '@/store/tabs'

const props = defineProps({
  billId: {
    type: String,
    default: null
  },
  paid: {
    type: Boolean,
    default: false
  }
})

const { t } = useI18n()
const { currency } = useMoney()
const tabsStore = useTabsStore()
const dialog = useDialog()
const { refundablePaymentMethods, getBillPayments, payments } = storeToRefs(tabsStore)

const openOptions = ref<boolean[]>([])

function getOptionsDataphone(paymentId: string): Option[] {
  return [
    {
      label: t('checkout.generate-receipt'),
      value: 'generate-receipt',
      onClick: () => generateBankReceipt(paymentId)
    },
    {
      label: t('checkout.refund-payment'),
      value: 'refund-payment',
      onClick: () => deletePaymentWithConfirmation(paymentId)
    }
  ]
}

function getPaymentType(paymentId: string) {
  const currentPayment = payments.value[paymentId]
  return currentPayment.type
}

function isDataphonePayment(paymentId: string) {
  const currentPayment = payments.value[paymentId]
  return (
    (currentPayment.type === 'dataphone' && !!currentPayment?.metadata?.receipt) ||
    (currentPayment.type === 'lastpay' && !!currentPayment?.metadata?.SaleToPOIResponse)
  )
}

function generateBankReceipt(paymentId: string) {
  const payment = payments.value[paymentId]
  TicketPrinter.printBankReceipt(payment)
}

function deletePaymentWithConfirmation(paymentId: string) {
  const paymentType = getPaymentType(paymentId)
  dialog({
    title: getDeletePaymentTitleByType(paymentType),
    content: getDeletePaymentContentByType(paymentType),
    secondaryLabel: t('ctas.cancel'),
    onConfirm: () => tabsStore.deletePayment({ billId: props.billId, paymentId })
  })
}

function getDeletePaymentTitleByType(paymentType: string) {
  return refundablePaymentMethods.value.includes(paymentType)
    ? t('checkout.refund-payment')
    : t('checkout.delete-payment')
}

function getDeletePaymentContentByType(paymentType: string) {
  return refundablePaymentMethods.value.includes(paymentType)
    ? t('checkout.refund-payment-content')
    : t('checkout.delete-payment-content')
}

const billPayments = computed(() => {
  if (!props.billId) return []
  return getBillPayments.value(props.billId)
})
</script>
